import { useContext } from 'react'

import {
  Button, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useBulkSelectImages from './hooks/useBulkSelectImages'

import ImagesView from './components/ImagesView'

const MODAL_KEY = 'BulkSelectImagesModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateEntity: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkSelectImagesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateEntity } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: { updateEntity: updateEntityFn }, entity,
  } = modalPayload

  const bulkSelectImagesPayload = useBulkSelectImages({ entity })

  const {
    hasFilteredImages,
    hasSelectedEntities,
    imageIdsKey,
    updating,
    selectedEntityIds,
  } = bulkSelectImagesPayload

  const updateEntityPayload = {
    callbacks: {
      action: updateEntityFn,
      afterAction: closeModal,
    },
    entityParams: { [imageIdsKey]: selectedEntityIds },
    toastText: 'Images Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Images" />

      <SidebarModal.Content>
        <SidebarNoticeBox boxProps={{ marginBottom: 'large' }}>
          <SidebarNoticeBox.Title>
            {hasFilteredImages
              ? 'Select Images in Order.'
              : 'No Images available'}

          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            {hasFilteredImages
              ? 'You can select multiple images by clicking on them. The order that you select them will be the order that they are used.'
              : 'Please add images to the Photos and Plans tab.'}
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        {hasFilteredImages && (
          <ImagesView bulkSelectImagesPayload={bulkSelectImagesPayload} />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!hasFilteredImages}
          loading={updating}
          onClick={() => updateEntity(updateEntityPayload)}
          size="large"
        >
          {hasSelectedEntities ? `Use Images (${selectedEntityIds.length})` : 'Select Images'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkSelectImagesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
